import React, {useState, ChangeEvent, useEffect} from 'react';
import {ContactFormModalProps} from './types';
import {useTranslation} from 'react-i18next';
import {some} from 'lodash';
import {FormErrorType} from "@sharedComponents/PantaForm/types";
import {getAllErrors} from "@sharedComponents/PantaForm/utils";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Icon from "@sharedComponents/Icon";
import InputText from "@sharedComponents/InputText";
import InputTextArea from "@sharedComponents/InputTextArea";
import {
    validateEmail,
    validateNewActivityMessage
} from "@apps/CompanyRegistrations/RegisterFlow/Forms/shared/Validation";
import './Scss/contact-form-modal-content.scss';

const ContactFormModalContent = (props: ContactFormModalProps) => {
    const {t} = useTranslation()
    const [email, setEmail] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [message, setMessage] = useState<string>('')
    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])

    const allErrors = getAllErrors(formErrors)
    const hasValidationErrors = !!(allErrors && allErrors.length > 0)
    const showRequestErrors =
        !hasValidationErrors &&
        props.requestErrors &&
        props.requestErrors.length > 0

    const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setEmail(value)
        const errors = validateEmail(value, true, formErrors)
        setFormErrors(errors)
    }

    const onChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setPhone(value)
    }

    const onChangeMessage = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value
        setMessage(value)
        const errors = validateNewActivityMessage(
            value.trim(),
            true,
            formErrors
        )
        setFormErrors(errors)
    }

    const onSubmit = () => {
        let errors = formErrors
        errors = validateEmail(email, true, errors)
        errors = validateNewActivityMessage(message.trim(), true, errors)
        setFormErrors(errors)

        if (!errors || errors.length === 0) {
            const formValues = {
                email: email,
                phone: phone,
                message: message,
                formType: props.formType,
            }
            props.onSubmit(formValues)
        }
    }

    useEffect(() => {
        if (!props.open) {
            //Reset form
            setEmail('')
            setPhone('')
            setMessage('')
            setFormErrors([])
        }
    }, [props.open])

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='contact-form-modal-content'>
            <h3>{t('connectCustomers.contactUs')}</h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>

            <div className='contact-form-modal-content__info-text'
                 dangerouslySetInnerHTML={{__html: props.cmsContent?.infoText || ''}}>
            </div>
            <div className='fields-wrapper'>
                <div className='fields-wrapper__field'>
                    <InputText
                        id={`contact-form-${props.formType}-email-input`}
                        label={t('connectCustomers.email')}
                        required={true}
                        value={email}
                        type="email"
                        autoComplete="email"
                        onChange={onChangeEmail}
                        maxLength={250}
                        hasError={some(formErrors, ['key', 'email'])}
                        errorText={uniqueErrors('email')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputText
                        id={`contact-form-${props.formType}-phone-input`}
                        label={t('connectCustomers.phone')}
                        value={phone}
                        autoComplete="tel"
                        onChange={onChangePhone}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputTextArea
                        id={`contact-form-${props.formType}-message-input`}
                        rows={3}
                        label={t('connectCustomers.newActivityMessage')}
                        required={true}
                        value={message}
                        onChange={onChangeMessage}
                        hasError={some(formErrors, ['key', 'message'])}
                        errorText={uniqueErrors('message')}
                    />
                </div>
            </div>

            {hasValidationErrors && (
                <div className='contact-form-modal-content__error'>
                    <Alert
                        type="danger"
                        text={t('connectCustomers.formErrors')}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            {showRequestErrors && (
                <div className='contact-form-modal-content__error'>
                    <Alert
                        type="danger"
                        compact={false}
                        text={t('error.somethingwrong')}
                        subtext={props.requestErrors}
                    />
                </div>
            )}

            <Button
                type={ButtonType.PRIMARY}
                text={t('button.sendMessage')}
                onClick={onSubmit}
                disabled={hasValidationErrors || props.submitting}
                loading={props.submitting}
            />

            <div className='contact-info'>
                <h5>{t('connectCustomers.customerServiceContactInfo')}</h5>
                <div className='contact-info__items'>
                    {props.cmsContent?.phone && (
                        <div className='contact-info__item'>
                            <div className='contact-info__icon'>
                                <Icon iconName="headset" size={16}/>
                            </div>
                            <span dangerouslySetInnerHTML={{__html: props.cmsContent?.phone || ''}}/>
                        </div>
                    )}
                    {props.cmsContent?.email && (
                        <div className='contact-info__item'>
                            <div className='contact-info__icon'>
                                <Icon iconName="paper-plane" size={16}/>
                            </div>
                            <span dangerouslySetInnerHTML={{__html: props.cmsContent?.email || ''}}/>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ContactFormModalContent